import { useState, useRef, useEffect } from "react";
import { ChevronDown, X, Check, SlidersHorizontal, Star } from "lucide-react";
import { DirectoryFilters as DirectoryFiltersType } from "@/types/directory";
import { getSpecialtyLabel } from "@/lib/specialties";

// Helper function to title-case a string
const toTitleCase = (str: string): string => {
  return str
    .toLowerCase()
    .split(" ")
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

export interface FilterState {
  specialties: string[];
  cities: string[];
  states: string[];
  minRating: number | null;
}

interface DirectoryFiltersProps {
  filters: DirectoryFiltersType | null;
  value: FilterState;
  onChange: (filters: FilterState) => void;
  loading?: boolean;
}

interface MultiSelectProps {
  label: string;
  values: string[];
  options: { label: string; value: string }[];
  onChange: (values: string[]) => void;
  placeholder?: string;
}

const MultiSelect = ({ label, values, options, onChange, placeholder = "All" }: MultiSelectProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const getDisplayText = () => {
    if (values.length === 0) return placeholder;
    if (values.length === 1) {
      return options.find(o => o.value === values[0])?.label || values[0];
    }
    return `${values.length} selected`;
  };

  const toggleValue = (value: string) => {
    if (values.includes(value)) {
      onChange(values.filter(v => v !== value));
    } else {
      onChange([...values, value]);
    }
  };

  return (
    <div ref={containerRef} className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center gap-2 px-3 py-2 border rounded-full text-sm transition-all ${
          values.length > 0 
            ? "bg-green-50 border-green-300 text-green-700" 
            : "bg-white border-grey-200 text-grey-700 hover:border-grey-300"
        }`}
      >
        <span className="max-w-[100px] truncate">{getDisplayText()}</span>
        {values.length > 0 ? (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onChange([]);
            }}
            className="p-0.5 hover:bg-green-100 rounded-full transition-colors"
            aria-label={`Clear ${label}`}
          >
            <X className="h-3.5 w-3.5" />
          </button>
        ) : (
          <ChevronDown className={`h-4 w-4 text-grey-400 transition-transform ${isOpen ? "rotate-180" : ""}`} />
        )}
      </button>

      {isOpen && (
        <>
          {/* Mobile: Full-screen overlay */}
          <div className="fixed inset-0 bg-black/20 z-40 sm:hidden" onClick={() => setIsOpen(false)} />
          <div className="fixed inset-x-4 bottom-4 top-auto sm:absolute sm:inset-auto sm:left-0 sm:bottom-auto sm:top-full sm:mt-2 w-auto sm:w-56 bg-white rounded-lg shadow-lg border border-grey-100 py-1 z-50 max-h-[60vh] sm:max-h-64 overflow-y-auto">
            {options.map((option) => {
              const isSelected = values.includes(option.value);
              return (
                <button
                  key={option.value}
                  onClick={() => toggleValue(option.value)}
                  className={`w-full flex items-center gap-3 px-4 py-3 sm:py-2 text-sm text-left hover:bg-grey-50 transition-colors ${
                    isSelected ? "text-green-600 font-medium" : "text-grey-700"
                  }`}
                >
                  <div
                    className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 ${
                      isSelected ? "bg-green-300 border-green-300" : "border-grey-300"
                    }`}
                  >
                    {isSelected && <Check className="h-3 w-3 text-white" />}
                  </div>
                  <span>{option.label}</span>
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

const RatingFilter = ({ value, onChange }: { value: number | null; onChange: (value: number | null) => void }) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const options = [
    { label: "4+ Stars", value: 4 },
    { label: "3+ Stars", value: 3 },
    { label: "2+ Stars", value: 2 },
  ];

  const selectedLabel = value 
    ? options.find(o => o.value === value)?.label 
    : "Rating";

  return (
    <div ref={containerRef} className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center gap-2 px-3 py-2 border rounded-full text-sm transition-all ${
          value 
            ? "bg-green-50 border-green-300 text-green-700" 
            : "bg-white border-grey-200 text-grey-700 hover:border-grey-300"
        }`}
      >
        <Star className="h-4 w-4" fill={value ? "currentColor" : "none"} />
        <span>{selectedLabel}</span>
        {value ? (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onChange(null);
            }}
            className="p-0.5 hover:bg-green-100 rounded-full transition-colors"
            aria-label="Clear rating filter"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        ) : (
          <ChevronDown className={`h-4 w-4 text-grey-400 transition-transform ${isOpen ? "rotate-180" : ""}`} />
        )}
      </button>

      {isOpen && (
        <>
          {/* Mobile: Full-screen overlay */}
          <div className="fixed inset-0 bg-black/20 z-40 sm:hidden" onClick={() => setIsOpen(false)} />
          <div className="fixed inset-x-4 bottom-4 top-auto sm:absolute sm:inset-auto sm:left-0 sm:bottom-auto sm:top-full sm:mt-2 w-auto sm:w-40 bg-white rounded-lg shadow-lg border border-grey-100 py-1 z-50">
            <button
              onClick={() => {
                onChange(null);
                setIsOpen(false);
              }}
              className={`w-full px-4 py-3 sm:py-2 text-sm text-left hover:bg-grey-50 transition-colors ${
                !value ? "text-green-600 font-medium" : "text-grey-700"
              }`}
            >
              Any Rating
            </button>
            {options.map((option) => (
              <button
                key={option.value}
                onClick={() => {
                  onChange(option.value);
                  setIsOpen(false);
                }}
                className={`w-full flex items-center gap-2 px-4 py-3 sm:py-2 text-sm text-left hover:bg-grey-50 transition-colors ${
                  value === option.value ? "text-green-600 font-medium" : "text-grey-700"
                }`}
              >
                <div className="flex items-center gap-0.5">
                  {[...Array(option.value)].map((_, i) => (
                    <Star key={i} className="h-3 w-3 text-yellow-400" fill="currentColor" />
                  ))}
                </div>
                <span>& up</span>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

const DirectoryFilters = ({ filters, value, onChange, loading }: DirectoryFiltersProps) => {
  const hasActiveFilters = 
    value.specialties.length > 0 || 
    value.cities.length > 0 || 
    value.states.length > 0 || 
    value.minRating !== null;

  const clearAllFilters = () => {
    onChange({
      specialties: [],
      cities: [],
      states: [],
      minRating: null,
    });
  };

  // Get cities based on selected states (union of all cities from selected states)
  const availableCities = value.states.length > 0
    ? value.states.flatMap(state => filters?.cities_by_state[state] || []).filter((city, index, self) => self.indexOf(city) === index)
    : filters?.cities || [];

  if (loading || !filters) {
    return (
      <div className="flex flex-wrap items-center gap-2">
        <div className="h-9 w-9 sm:w-24 bg-grey-100 rounded-full animate-pulse" />
        <div className="h-9 w-20 bg-grey-100 rounded-full animate-pulse" />
        <div className="h-9 w-16 bg-grey-100 rounded-full animate-pulse" />
        <div className="h-9 w-16 bg-grey-100 rounded-full animate-pulse hidden sm:block" />
      </div>
    );
  }

  return (
    <div className="overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0 sm:overflow-visible">
      <div className="flex items-center gap-2 min-w-max sm:min-w-0 sm:flex-wrap">
        <div className="flex items-center gap-1 text-grey-500 mr-1 flex-shrink-0">
          <SlidersHorizontal className="h-4 w-4" />
          <span className="text-sm font-medium hidden sm:inline">Filters</span>
        </div>

        <MultiSelect
          label="Specialty"
          values={value.specialties}
          options={filters.specialties.map(s => ({ label: getSpecialtyLabel(s), value: s }))}
          onChange={(specialties) => onChange({ ...value, specialties })}
          placeholder="Specialty"
        />

        <MultiSelect
          label="State"
          values={value.states}
          options={filters.states.map(s => ({ label: toTitleCase(s), value: s }))}
          onChange={(states) => onChange({ ...value, states, cities: [] })} // Reset cities when states change
          placeholder="State"
        />

        <MultiSelect
          label="City"
          values={value.cities}
          options={availableCities.map(c => ({ label: toTitleCase(c), value: c }))}
          onChange={(cities) => onChange({ ...value, cities })}
          placeholder="City"
        />

        <RatingFilter
          value={value.minRating}
          onChange={(minRating) => onChange({ ...value, minRating })}
        />

        {hasActiveFilters && (
          <button
            onClick={clearAllFilters}
            className="flex items-center gap-1 px-3 py-2 text-sm text-grey-600 hover:text-grey-800 hover:bg-grey-100 rounded-full transition-colors flex-shrink-0"
          >
            <X className="h-4 w-4" />
            <span className="hidden sm:inline">Clear all</span>
          </button>
        )}
      </div>
    </div>
  );
};

export default DirectoryFilters;
